import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building, ArrowRight, CheckCircle, Truck } from 'lucide-react';

const BRAND_NAME = 'Himil Fleet';
const BRAND_LOGO_SRC = '/Himil-logo.png';

const CompanyRegistration = () => {
  const [step, setStep] = useState('intro');
  const navigate = useNavigate();

  const handleGetStarted = () => {
    setStep('options');
  };

  const handleOptionSelect = (option) => {
    if (option === 'new') {
      // Navigate to onboarding wizard
      navigate('/onboarding');
    } else if (option === 'existing') {
      // Navigate to login with special message
      navigate('/login', { 
        state: { 
          message: 'Please sign in with your existing company account.' 
        } 
      });
    }
  };

  if (step === 'options') {
    return (
      <div
        className="bg-gradient-to-br from-blue-50 to-indigo-100 flex md:items-center md:justify-center overflow-y-auto"
        style={{ minHeight: '100dvh' }} // mobile-safe vh
      >
        <div className="w-full max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 flex items-center justify-center">
              <button
                onClick={() => navigate('/')}
                className="hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
                title="Go to landing page"
              >
                <img src={BRAND_LOGO_SRC} alt={`${BRAND_NAME} logo`} className="h-full w-auto" />
              </button>
            </div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Join {BRAND_NAME}
            </h2>
            <p className="mt-2 text-lg text-gray-600">How would you like to get started?</p>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <button
              onClick={() => handleOptionSelect('new')}
              className="group bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow border-2 border-transparent hover:border-brand-mid focus:outline-none focus:ring-2 focus:ring-brand-light"
            >
              <div className="text-center">
                <div className="mx-auto h-12 w-12 bg-green-100 rounded-full flex items-center justify-center group-hover:bg-green-200 transition-colors">
                  <Building className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="mt-4 text-lg font-medium text-gray-900">New Company</h3>
                <p className="mt-2 text-sm text-gray-500">
                  Set up a new trucking company account with our guided onboarding process.
                </p>
                <div className="mt-4 flex items-center justify-center text-brand-mid group-hover:text-brand-dark">
                  <span className="text-sm font-medium">Get Started</span>
                  <ArrowRight className="ml-2 h-4 w-4" />
                </div>
              </div>
            </button>

            <button
              onClick={() => handleOptionSelect('existing')}
              className="group bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow border-2 border-transparent hover:border-brand-mid focus:outline-none focus:ring-2 focus:ring-brand-light"
            >
              <div className="text-center">
                <div className="mx-auto h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                  <CheckCircle className="h-6 w-6 text-brand-mid" />
                </div>
                <h3 className="mt-4 text-lg font-medium text-gray-900">Existing Company</h3>
                <p className="mt-2 text-sm text-gray-500">
                  Sign in to your existing company account to access your dashboard.
                </p>
                <div className="mt-4 flex items-center justify-center text-brand-mid group-hover:text-brand-dark">
                  <span className="text-sm font-medium">Sign In</span>
                  <ArrowRight className="ml-2 h-4 w-4" />
                </div>
              </div>
            </button>
          </div>

          <div className="mt-8 text-center pb-6"> {/* pb for safe bottom space */}
            <p className="text-sm text-gray-500">
              Need help deciding?{' '}
              <a href="#" className="text-brand-mid hover:text-brand-dark font-medium">
                Contact our support team
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl w-full space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Welcome to {BRAND_NAME}
          </h2>
          <p className="mt-2 text-lg text-gray-600">
            Streamline your fleet compliance, document management, and maintenance tracking
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="h-8 w-8 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <CheckCircle className="h-5 w-5 text-blue-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Compliance Tracking</h3>
            <p className="text-sm text-gray-500">
              Track certifications, registrations, and documents with auto-expiration alerts.
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="h-8 w-8 bg-green-100 rounded-lg flex items-center justify-center mb-4">
              <Building className="h-5 w-5 text-green-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Fleet Management</h3>
            <p className="text-sm text-gray-500">
              Manage vehicles, maintenance schedules, and inspections in one place.
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="h-8 w-8 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
              <Truck className="h-5 w-5 text-purple-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Smart Reporting</h3>
            <p className="text-sm text-gray-500">
              Generate reports and view insights on performance and compliance.
            </p>
          </div>
        </div>


        {/* Benefits */}
        <div className="bg-white rounded-lg shadow-md p-8">
          <h3 className="text-xl font-medium text-gray-900 mb-6 text-center">
            Why Choose Our Platform?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-gray-900">DOT Compliance Made Easy</h4>
                  <p className="text-sm text-gray-500">Stay compliant with federal regulations and avoid costly violations</p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-gray-900">Automated Reminders</h4>
                  <p className="text-sm text-gray-500">Never miss important deadlines with smart notification system</p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-gray-900">Centralized Documents</h4>
                  <p className="text-sm text-gray-500">Store and access all your compliance documents in one secure location</p>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-gray-900">Real-time Dashboard</h4>
                  <p className="text-sm text-gray-500">Monitor your fleet's compliance status at a glance</p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-gray-900">Mobile Access</h4>
                  <p className="text-sm text-gray-500">Access your data anywhere, anytime from any device</p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-gray-900">Expert Support</h4>
                  <p className="text-sm text-gray-500">Get help from our team of compliance and technology experts</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <button
            onClick={handleGetStarted}
            className="btn-primary px-8 py-3 text-base"
          >
            Get Started Today
            <ArrowRight className="ml-2 h-5 w-5" />
          </button>
          <p className="mt-4 text-sm text-gray-500">
            Already have an account?{' '}
            <Link to="/login" className="text-brand-mid hover:text-brand-dark font-medium">
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CompanyRegistration;
